import * as THREE from 'three';
import type { Terrain } from '../game/Terrain';

type Phase = 'idle' | 'warning' | 'impact' | 'dust';

/**
 * Giant-foot stomp hazard. Schedules a random stomp on a [intervalMin,
 * intervalMax] cadence. Each stomp plays out in three beats:
 *   1. Warning  — a dark shadow grows from 0 → 2m over 2s. Ants are
 *                 continuously scattered so they visibly flee.
 *   2. Impact   — one frame of 50 dps × 0.5s across 2.2m radius. A light
 *                 brown dust disk is born.
 *   3. Dust     — disk radius 2.2 → 4m and opacity 0.6 → 0 over 0.6s.
 * No foot mesh: the shadow and dust carry the beat.
 */
export class FootStomp {
  readonly group = new THREE.Group();

  private terrain: Terrain;
  private intervalMin: number;
  private intervalMax: number;

  private phase: Phase = 'idle';
  private phaseT = 0;
  private nextAt = 0;

  private targetX = 0;
  private targetZ = 0;

  private shadow: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  private dust: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;

  private static readonly WARN_DURATION = 2.0;
  private static readonly WARN_MAX_RADIUS = 2.0;
  private static readonly IMPACT_RADIUS = 2.2;
  private static readonly IMPACT_DPS = 50;
  private static readonly IMPACT_WINDOW = 0.5;
  private static readonly DUST_DURATION = 0.6;
  private static readonly DUST_MAX_RADIUS = 4.0;
  private static readonly DUST_START_OPACITY = 0.6;
  private static readonly BOUND = 36;

  constructor(terrain: Terrain, opts?: { intervalMin?: number; intervalMax?: number }) {
    this.terrain = terrain;
    this.intervalMin = opts?.intervalMin ?? 60;
    this.intervalMax = opts?.intervalMax ?? 180;
    this.group.name = 'foot-stomp';

    // Shadow disk — starts invisible, a CircleGeometry(1, 32) we scale.
    const shadowGeo = new THREE.CircleGeometry(1, 32);
    shadowGeo.rotateX(-Math.PI / 2);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    this.shadow = new THREE.Mesh(shadowGeo, shadowMat);
    this.shadow.visible = false;
    this.shadow.renderOrder = 2;
    this.group.add(this.shadow);

    // Dust disk — light brown, tints over the ground after impact.
    const dustGeo = new THREE.CircleGeometry(1, 32);
    dustGeo.rotateX(-Math.PI / 2);
    const dustMat = new THREE.MeshBasicMaterial({
      color: 0xc8a878,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.dust = new THREE.Mesh(dustGeo, dustMat);
    this.dust.visible = false;
    this.dust.renderOrder = 3;
    this.group.add(this.dust);

    this.scheduleNext(0);
  }

  update(
    dt: number,
    elapsed: number,
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
    scatter: (x: number, z: number, radius: number, force?: number) => void,
  ): void {
    if (this.phase === 'idle') {
      if (elapsed >= this.nextAt) this.beginWarning();
      return;
    }

    this.phaseT += dt;

    if (this.phase === 'warning') {
      const t = Math.min(this.phaseT / FootStomp.WARN_DURATION, 1);
      const r = FootStomp.WARN_MAX_RADIUS * t;
      this.shadow.scale.set(Math.max(r, 0.0001), 1, Math.max(r, 0.0001));
      // Nudge ants out of the way continuously during the warn-up.
      scatter(this.targetX, this.targetZ, 3.5, 0.8);
      if (this.phaseT >= FootStomp.WARN_DURATION) this.triggerImpact(damage);
      return;
    }

    if (this.phase === 'impact') {
      // Impact lasts one frame — already applied in triggerImpact. Move on.
      this.beginDust();
      return;
    }

    if (this.phase === 'dust') {
      const t = Math.min(this.phaseT / FootStomp.DUST_DURATION, 1);
      const r = FootStomp.IMPACT_RADIUS + (FootStomp.DUST_MAX_RADIUS - FootStomp.IMPACT_RADIUS) * t;
      this.dust.scale.set(r, 1, r);
      this.dust.material.opacity = FootStomp.DUST_START_OPACITY * (1 - t);
      if (this.phaseT >= FootStomp.DUST_DURATION) {
        this.dust.visible = false;
        this.dust.material.opacity = 0;
        this.phase = 'idle';
        this.phaseT = 0;
        this.scheduleNext(elapsed);
      }
    }
  }

  private scheduleNext(now: number): void {
    const span = this.intervalMax - this.intervalMin;
    const wait = this.intervalMin + Math.random() * Math.max(0, span);
    this.nextAt = now + wait;
  }

  private beginWarning(): void {
    this.targetX = (Math.random() * 2 - 1) * FootStomp.BOUND;
    this.targetZ = (Math.random() * 2 - 1) * FootStomp.BOUND;
    const y = this.terrain.getHeight(this.targetX, this.targetZ);

    this.shadow.position.set(this.targetX, y + 0.01, this.targetZ);
    this.shadow.scale.set(0.0001, 1, 0.0001);
    this.shadow.material.opacity = 0.5;
    this.shadow.visible = true;

    this.dust.position.set(this.targetX, y + 0.015, this.targetZ);
    this.dust.visible = false;
    this.dust.material.opacity = 0;

    this.phase = 'warning';
    this.phaseT = 0;
  }

  private triggerImpact(
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
  ): void {
    // One-shot lethal pulse: 50 dps × 0.5s across 2.2m.
    damage(
      this.targetX,
      this.targetZ,
      FootStomp.IMPACT_RADIUS,
      FootStomp.IMPACT_DPS,
      FootStomp.IMPACT_WINDOW,
    );
    this.shadow.visible = false;
    this.phase = 'impact';
    this.phaseT = 0;
  }

  private beginDust(): void {
    this.dust.scale.set(FootStomp.IMPACT_RADIUS, 1, FootStomp.IMPACT_RADIUS);
    this.dust.material.opacity = FootStomp.DUST_START_OPACITY;
    this.dust.visible = true;
    this.phase = 'dust';
    this.phaseT = 0;
  }
}
